import type {
  AbilityName,
  CharacterSheet,
  Provenance,
  SheetResource,
} from '@rollcall/contracts'
import type { TFunction } from 'i18next'
import { ABILITY_KEYS, ABILITY_NAMES } from '@rollcall/rules'
import { contentName, resourceName } from './ui/labels.js'
import { signedNumber } from './ui/provenance.js'

/**
 * Печатный лист: что стоит в каждом поле бланка (D64).
 *
 * Здесь ни одного расчёта — как и на экране. Числа приходят свёрткой
 * с сервера (D4), а этот файл только раскладывает их по полям бланка
 * и форматирует теми же функциями и строками, что экран: со знаком —
 * `signedNumber`, названия — `contentName`, кость хитов, тип урона
 * и восстановление ресурса — строками вкладок «Бой» и «Персонаж».
 * Разойдись форматирование — на бумаге стояло бы «-1», на экране «−1»,
 * и сверять их было бы мучением.
 *
 * Пустая строка — поле, которое приложение не ведёт: имя игрока,
 * мировоззрение, вдохновение, спасброски от смерти. Бланк печатает
 * его пустым, чтобы вписать от руки, а не прячет: игрок за столом
 * ищет поле на привычном месте.
 */

/** Сколько строк атак помещается в рамку образца. */
export const ATTACK_ROWS = 6
/** Сколько счётчиков ресурсов помещается в нижнюю рамку: два столбца по пять. */
export const RESOURCE_ROWS = 10

export type PrintMark = {
  /** Путь величины на листе: по нему тест сверяет с экраном. */
  readonly path: string
  readonly label: string
  readonly value: string
  readonly proficient: boolean
}

export type PrintAbility = {
  readonly name: AbilityName
  /** Путь значения на листе: под ним экран показывает модификатор крупно. */
  readonly path: string
  readonly label: string
  /** Значение — мелко, в выступе рамки. */
  readonly score: string
  /** Модификатор — крупно: им пользуются в каждом броске. */
  readonly mod: string
  readonly save: PrintMark
  readonly skills: readonly PrintMark[]
}

export type PrintAttack = {
  readonly id: string
  readonly name: string
  /** Ближний или дальний бой — строкой вкладки «Бой». */
  readonly kind: string
  readonly bonusPath: string
  readonly bonus: string
  readonly damage: string
  readonly type: string
}

export type PrintResource = {
  readonly id: string
  readonly name: string
  readonly current: number
  readonly max: number
  readonly recovery: string
}

export type PrintSheet = {
  readonly name: string
  readonly header: {
    readonly classLevel: string
    readonly subclass: string
    readonly player: string
    readonly species: string
    readonly background: string
    readonly originFeat: string
    readonly alignment: string
    readonly xp: string
    readonly code: string
  }
  readonly proficiencyBonus: string
  readonly inspiration: string
  readonly passivePerception: string
  readonly abilities: readonly PrintAbility[]
  readonly ac: string
  readonly initiative: string
  readonly speed: string
  readonly hp: {
    readonly max: string
    readonly current: string
    /** Временные хиты сейчас. Сколько их было выдано, приложение не хранит. */
    readonly temp: string
  }
  readonly hitDice: { readonly total: string; readonly spent: string }
  readonly attacks: readonly PrintAttack[]
  /** Атаки, которым не хватило строк: о них сказано, а не молчится. */
  readonly attacksMore: number
  /** `null` — чисел заклинателя у листа нет, и поля печатаются прочерком. */
  readonly spellcasting: {
    readonly ability: string
    readonly saveDc: string
    readonly attack: string
  } | null
  readonly coins: { readonly gp: string }
  readonly resources: readonly PrintResource[]
  readonly resourcesMore: number
}

function numberAt(sheet: CharacterSheet, path: string): number | null {
  const value: Provenance['value'] | undefined = sheet.values[path]?.value
  return typeof value === 'number' ? value : null
}

/** Число без знака — как КД и хиты на экране; нет величины — пусто. */
function plain(sheet: CharacterSheet, path: string): string {
  const value = numberAt(sheet, path)
  return value === null ? '' : String(value)
}

/** Число со знаком — как модификаторы на экране. */
function signed(sheet: CharacterSheet, path: string): string {
  const value = numberAt(sheet, path)
  return value === null ? '' : signedNumber(value)
}

/** Запись листа заданного вида: вид, класс, подкласс, предыстория. */
function named(
  t: TFunction,
  sheet: CharacterSheet,
  type: 'species' | 'class' | 'subclass' | 'background',
): string {
  const found = Object.values(sheet.sources).find((source) => source.type === type)
  return found === undefined ? '' : contentName(t, found.id, found.name)
}

/** Восстановление — той же строкой, что под счётчиком на экране. */
function recoveryOf(t: TFunction, resource: SheetResource): string {
  const rule = resource.recovery
  if (rule.kind === 'none') return t('resources.recovery.none')
  if (rule.kind === 'long_rest') return t('resources.recovery.long')
  return rule.uses === null
    ? t('resources.recovery.short')
    : t('resources.recovery.shortSome', { uses: rule.uses })
}

/** Числа заклинателя — по тому же признаку, что на вкладке «Персонаж». */
const SPELL_VALUES = ['spell.save_dc', 'spell.attack'] as const

export function printSheet(t: TFunction, sheet: CharacterSheet): PrintSheet {
  const className = named(t, sheet, 'class')
  const xp = sheet.xp
  const abilities = ABILITY_NAMES.map((name): PrintAbility => {
    const key = ABILITY_KEYS[name]
    const save = sheet.saves.find((item) => item.ability === name)
    return {
      name,
      path: `abilities.${key}.score`,
      label: t(`abilities.${name}`),
      score: plain(sheet, `abilities.${key}.score`),
      // Модификатор — тот же, что крупно стоит на экране: из контекста
      // листа, а не пересчитанный из значения здесь.
      mod: signedNumber(sheet.context.abilities[key].mod),
      save: {
        path: save?.path ?? `saves.${key}`,
        label: t('print.save'),
        value: save === undefined ? '' : signed(sheet, save.path),
        proficient: save?.proficient ?? false,
      },
      // Навыки — по характеристике из самой записи навыка, а не по
      // списку бланка: в образце Запугивание стоит и под Силой,
      // и под Харизмой, а по правилам 2024 года оно харизмы.
      skills: sheet.skills
        .filter((skill) => skill.ability === name)
        .map((skill) => ({
          path: skill.path,
          label: contentName(t, skill.id, skill.name),
          value: signed(sheet, skill.path),
          proficient: skill.proficient,
        })),
    }
  })

  const attacks = sheet.combat.attacks.map((attack): PrintAttack => {
    // Прибавка к урону — тем же видом, что на вкладке «Бой»: «1d8+3».
    const extra = attack.damagePath === null ? null : numberAt(sheet, attack.damagePath)
    const bonus =
      extra === null || extra === 0 ? '' : `${extra > 0 ? '+' : '−'}${Math.abs(extra)}`
    return {
      id: attack.id,
      name: contentName(t, attack.id, attack.name),
      kind: t(attack.ranged ? 'combat.ranged' : 'combat.melee'),
      bonusPath: attack.attackPath,
      bonus: signed(sheet, attack.attackPath),
      damage: `${attack.damage.notation}${bonus}`,
      type: t(`trait.damage.${attack.damage.type}`, {
        defaultValue: attack.damage.type,
      }),
    }
  })

  const caster = SPELL_VALUES.every((path) => sheet.values[path] !== undefined)
  const resources = sheet.resources.map((resource): PrintResource => ({
    id: resource.id,
    name: resourceName(t, resource),
    current: resource.current,
    max: resource.max,
    recovery: recoveryOf(t, resource),
  }))

  const hp = sheet.combat.hp
  const originFeat = sheet.originFeat ?? null

  return {
    name: sheet.character.name,
    header: {
      classLevel:
        className === ''
          ? t('print.levelOnly', { level: sheet.level })
          : t('print.classLevel', { name: className, level: sheet.level }),
      subclass: named(t, sheet, 'subclass'),
      player: '',
      species: named(t, sheet, 'species'),
      background: named(t, sheet, 'background'),
      originFeat:
        originFeat === null ? '' : contentName(t, originFeat.id, originFeat.name),
      alignment: '',
      xp:
        xp.nextAt === null
          ? String(xp.total)
          : t('print.xpOf', { total: xp.total, next: xp.nextAt }),
      code: sheet.character.code,
    },
    proficiencyBonus: signed(sheet, 'proficiency_bonus'),
    inspiration: '',
    passivePerception: plain(sheet, 'passive.perception'),
    abilities,
    ac: plain(sheet, 'ac'),
    initiative: signed(sheet, 'initiative'),
    speed: plain(sheet, 'speed'),
    hp: {
      max: String(hp.max),
      current: String(hp.current),
      temp: hp.temp > 0 ? String(hp.temp) : '',
    },
    hitDice: {
      total:
        hp.hitDie === null
          ? ''
          : t('combat.hitDiceValue', { count: hp.hitDice, die: hp.hitDie }),
      spent: hp.hitDie === null ? '' : String(hp.hitDiceSpent),
    },
    attacks: attacks.slice(0, ATTACK_ROWS),
    attacksMore: Math.max(0, attacks.length - ATTACK_ROWS),
    spellcasting: caster
      ? {
          ability:
            sheet.spells.ability === null ? '' : t(`abilities.${sheet.spells.ability}`),
          saveDc: plain(sheet, 'spell.save_dc'),
          attack: signed(sheet, 'spell.attack'),
        }
      : null,
    coins: { gp: String(sheet.inventory.coinsGp) },
    resources: resources.slice(0, RESOURCE_ROWS),
    resourcesMore: Math.max(0, resources.length - RESOURCE_ROWS),
  }
}

/**
 * Как набрана строка: от этого зависит средняя ширина знака.
 *
 * Коэффициенты — доля кегля на знак, измеренная в Chromium на PT Sans
 * и PT Serif по строкам бланка («Посвящённый в магию», «Чародейское
 * восстановление», «ЧЕРТА ПРОИСХОЖДЕНИЯ»), с запасом в пять процентов:
 * строчные — 0.49…0.56, заглавные — 0.57…0.62, заглавные жирные
 * с разрядкой подписи — 0.63…0.68, жирные цифры с засечками — около 0.6.
 */
export const GLYPH_WIDTH = {
  text: 0.58,
  upper: 0.65,
  label: 0.71,
  number: 0.63,
} as const

export type TextStyle = keyof typeof GLYPH_WIDTH

/**
 * Кегль строки, которой отведено место бланка (D64).
 *
 * Мерить текст в браузере до печати можно, но тогда лист перестаёт
 * быть функцией листа: тот же персонаж печатается по-разному в разных
 * окнах. Поэтому кегль считается от длины строки: длинное имя
 * набирается мельче, а предел снизу держит читаемость. Что не влезло
 * и в нижний кегль, обрезается многоточием — на вторую страницу
 * не переползает ничего.
 *
 * `widthMm` — ширина поля, `maxPt` и `minPt` — пределы кегля.
 */
export function fitPt(
  text: string,
  widthMm: number,
  maxPt: number,
  minPt: number,
  style: TextStyle = 'text',
): number {
  if (text.length === 0) return maxPt
  const ptPerMm = 72 / 25.4
  const fit = (widthMm * ptPerMm) / (text.length * GLYPH_WIDTH[style])
  return Math.max(minPt, Math.min(maxPt, Math.floor(fit * 2) / 2))
}

/**
 * Строка, которой разрешено встать в две строки: имя персонажа.
 *
 * Одной строкой — пока кегль не мельче `wrapBelow`; дальше текст
 * переносится, и кегль считается от половины длины, с запасом
 * на неровный перенос по словам.
 */
export function fitLines(
  text: string,
  widthMm: number,
  maxPt: number,
  wrapBelow: number,
  minPt: number,
  style: TextStyle = 'text',
): { readonly size: number; readonly lines: 1 | 2 } {
  const single = fitPt(text, widthMm, maxPt, wrapBelow, style)
  if (fitPt(text, widthMm, maxPt, 0, style) >= wrapBelow)
    return { size: single, lines: 1 }
  return { size: fitPt(text, widthMm * 2 * 0.75, wrapBelow, minPt, style), lines: 2 }
}
